import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getActiveHellocians } from '../../../services/hellocian.service';
import { getCategories, getTags, createTag } from '../../../services/category.service';
import { toast } from 'react-toastify';

const Step1 = ({ formData, setFormData, fileInputRef, setCurrentStep }) => {
  const queryClient = useQueryClient();

  const [hellocianDropdownOpen, setHellocianDropdownOpen] = useState(false);
  const [searchHellocian, setSearchHellocian] = useState('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [searchCategory, setSearchCategory] = useState('');

  // New tag creation state
  const [newTagInput, setNewTagInput] = useState('');
  const [showNewTagInput, setShowNewTagInput] = useState(false);

  // --- API Data ---
  const { data: hellocianData, isLoading: loadingHellocians } = useQuery({
    queryKey: ['activeHellocians'],
    queryFn: getActiveHellocians,
  });

  const { data: categoryData, isLoading: loadingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const { data: tagData, isLoading: loadingTags } = useQuery({
    queryKey: ['tags'],
    queryFn: getTags,
  });

  const hellocians = hellocianData?.hellocians || [];
  const categories = categoryData?.categories || [];
  const tags = tagData?.tags || [];

  // --- Create Tag Mutation ---
  const createTagMutation = useMutation({
    mutationFn: (name) => createTag(name),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      // Auto-select the newly created tag
      const newTag = data.tag;
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, newTag._id]
      }));
      setNewTagInput('');
      setShowNewTagInput(false);
      toast.success(`Tag "${newTag.name}" created and selected!`);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to create tag');
    }
  });

  // --- Filters ---
  const filteredHellocians = hellocians.filter(h =>
    `${h.firstName} ${h.lastName}`.toLowerCase().includes(searchHellocian.toLowerCase())
  );

  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(searchCategory.toLowerCase())
  );

  // --- Toggle Selection Handlers ---
  const toggleHellocian = (hellocian) => {
    const isSelected = formData.selectedHellocians.includes(hellocian._id);
    setFormData(prev => ({
      ...prev,
      selectedHellocians: isSelected
        ? prev.selectedHellocians.filter(id => id !== hellocian._id)
        : [...prev.selectedHellocians, hellocian._id]
    }));
  };

  const toggleCategory = (category) => {
    const isSelected = formData.selectedCategories.includes(category._id);
    setFormData(prev => ({
      ...prev,
      selectedCategories: isSelected
        ? prev.selectedCategories.filter(id => id !== category._id)
        : [...prev.selectedCategories, category._id]
    }));
  };

  const toggleTag = (tag) => {
    const isSelected = formData.keywords.includes(tag._id);
    setFormData(prev => ({
      ...prev,
      keywords: isSelected
        ? prev.keywords.filter(id => id !== tag._id)
        : [...prev.keywords, tag._id]
    }));
  };

  // --- Image Handlers ---
  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', 'foogles'); // Using the same preset as in ProjectSubmission
    const cloudName = 'djstk2ukm';

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: data
      });
      const cloudData = await res.json();
      if (cloudData.secure_url) {
        return cloudData.secure_url;
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Cloudinary upload error:', err);
      throw err;
    }
  };

  const handleImageUpload = async (e) => {
    let files = Array.from(e.target.files);
    if (!files.length) return;

    const currentCount = formData.images.length;
    const remaining = 4 - currentCount;

    if (remaining <= 0) {
      toast.warning('Maximum of 4 images allowed.');
      return;
    }

    if (files.length > remaining) {
      toast.warning(`Only ${remaining} more image${remaining > 1 ? 's' : ''} can be added. Skipping extras.`);
      files = files.slice(0, remaining);
    }

    // First, add placeholders with loading state
    const newImages = files.map(file => ({
      id: Math.random().toString(36).substring(2, 9),
      file,
      loading: true,
      preview: URL.createObjectURL(file)
    }));

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));

    // Then upload each one
    for (const imgObj of newImages) {
      try {
        const url = await uploadToCloudinary(imgObj.file);
        setFormData(prev => ({
          ...prev,
          images: prev.images.map(img => 
            img.id === imgObj.id ? { ...img, preview: url, loading: false } : img
          )
        }));
      } catch (err) {
        toast.error(`Failed to upload ${imgObj.file.name}`);
        setFormData(prev => ({
          ...prev,
          images: prev.images.filter(img => img.id !== imgObj.id)
        }));
      }
    }
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    const target = newImages[index];
    if (target.preview && target.preview.startsWith('blob:')) {
      URL.revokeObjectURL(target.preview);
    }
    newImages.splice(index, 1);
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  // --- Helper to get hellocian object by ID ---
  const getHellocianById = (id) => hellocians.find(h => h._id === id);
  const getCategoryById = (id) => categories.find(c => c._id === id);
  const getTagById = (id) => tags.find(t => t._id === id);

  const isNextEnabled = () => {
    const baseValid = formData.title &&
      formData.keywords.length > 0 &&
      formData.selectedHellocians.length > 0 &&
      formData.selectedCategories.length > 0;
    
    const isUploading = formData.images.some(img => img.loading);
    return baseValid && !isUploading;
  };

  return (
    <div className="bg-white dark:bg-[#191c22] p-6 rounded-lg shadow-md border dark:border-[#32353c] transition-colors duration-200">
      {/* Dropdowns Row */}
      <div className="mb-6 flex gap-4 flex-wrap">

        {/* --- Hellocian Dropdown --- */}
        <div className="relative">
          <button
            type="button"
            onClick={() => { setHellocianDropdownOpen(!hellocianDropdownOpen); setCategoryDropdownOpen(false); }}
            className="flex items-center justify-between w-56 px-4 py-2 bg-[#174568] dark:bg-[#1d2026] text-white rounded-md focus:outline-none border dark:border-[#32353c] transition-colors"
          >
            <span>Select Hellocian</span>
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {hellocianDropdownOpen && (
            <div className="absolute z-20 mt-1 w-80 bg-white dark:bg-[#191c22] rounded-md shadow-lg p-2 border border-gray-200 dark:border-[#32353c]">
              <div className="p-2 border-b dark:border-[#32353c]">
                <input
                  type="text"
                  placeholder="Search Hellocians..."
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-[#0b0e14] dark:border-[#32353c] dark:text-white"
                  value={searchHellocian}
                  onChange={e => setSearchHellocian(e.target.value)}
                />
              </div>
              <div className="max-h-60 overflow-y-auto">
                {loadingHellocians ? (
                  <p className="text-gray-400 text-sm p-3">Loading...</p>
                ) : filteredHellocians.length === 0 ? (
                  <p className="text-gray-400 text-sm p-3">No activated hellocians found</p>
                ) : (
                  filteredHellocians.map(hellocian => (
                    <div
                      key={hellocian._id}
                      className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-[#1d2026] cursor-pointer rounded-md transition-colors"
                      onClick={() => toggleHellocian(hellocian)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedHellocians.includes(hellocian._id)}
                        readOnly
                        className="mr-2"
                      />
                      {hellocian.profilePicture ? (
                        <img
                          src={hellocian.profilePicture}
                          alt={`${hellocian.firstName} ${hellocian.lastName}`}
                          className="w-8 h-8 rounded-full mr-2 object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full mr-2 bg-[#174568] flex items-center justify-center text-white text-xs font-bold">
                          {hellocian.firstName[0]}{hellocian.lastName[0]}
                        </div>
                      )}
                      <span className="text-sm dark:text-gray-300">{hellocian.firstName} {hellocian.lastName}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* --- Category Dropdown --- */}
        <div className="relative">
          <button
            type="button"
            onClick={() => { setCategoryDropdownOpen(!categoryDropdownOpen); setHellocianDropdownOpen(false); }}
            className="flex items-center justify-between w-56 px-4 py-2 bg-[#174568] dark:bg-[#1d2026] text-white rounded-md focus:outline-none border dark:border-[#32353c] transition-colors"
          >
            <span>Select Categories</span>
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {categoryDropdownOpen && (
            <div className="absolute z-20 mt-1 w-80 bg-white dark:bg-[#191c22] rounded-md shadow-lg p-2 border border-gray-200 dark:border-[#32353c]">
              <div className="p-2 border-b dark:border-[#32353c]">
                <input
                  type="text"
                  placeholder="Search Categories..."
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-[#0b0e14] dark:border-[#32353c] dark:text-white"
                  value={searchCategory}
                  onChange={e => setSearchCategory(e.target.value)}
                />
              </div>
              <div className="max-h-60 overflow-y-auto">
                {loadingCategories ? (
                  <p className="text-gray-400 text-sm p-3">Loading...</p>
                ) : filteredCategories.length === 0 ? (
                  <p className="text-gray-400 text-sm p-3">No categories found</p>
                ) : (
                  filteredCategories.map(category => (
                    <div
                      key={category._id}
                      className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-[#1d2026] cursor-pointer rounded-md transition-colors"
                      onClick={() => toggleCategory(category)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedCategories.includes(category._id)}
                        readOnly
                        className="mr-2"
                      />
                      <span className="text-sm dark:text-gray-300">{category.name}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Selected Hellocians display */}
      {formData.selectedHellocians.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium uppercase tracking-wide">Selected Hellocians</p>
          <div className="flex flex-wrap gap-2">
            {formData.selectedHellocians.map(id => {
              const h = getHellocianById(id);
              if (!h) return null;
              return (
                <div key={id} className="inline-flex items-center bg-blue-50 dark:bg-[#1d2026] border border-blue-200 dark:border-[#32353c] rounded-full px-3 py-1 transition-colors">
                  {h.profilePicture ? (
                    <img src={h.profilePicture} alt={`${h.firstName}`} className="w-5 h-5 rounded-full mr-1.5 object-cover" />
                  ) : (
                    <div className="w-5 h-5 rounded-full mr-1.5 bg-[#174568] flex items-center justify-center text-white text-xs">
                      {h.firstName[0]}
                    </div>
                  )}
                  <span className="text-sm text-blue-800 dark:text-blue-300">{h.firstName} {h.lastName}</span>
                  <button onClick={() => toggleHellocian(h)} className="ml-1.5 text-blue-400 dark:text-blue-500 hover:text-red-500 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Categories display */}
      {formData.selectedCategories.length > 0 && (
        <div className="mb-6">
          <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Selected Categories</p>
          <div className="flex flex-wrap gap-2">
            {formData.selectedCategories.map(id => {
              const c = getCategoryById(id);
              if (!c) return null;
              return (
                <div key={id} className="inline-flex items-center bg-gray-100 dark:bg-[#1d2026] rounded-full px-3 py-1 transition-colors">
                  <span className="text-sm dark:text-gray-300">{c.name}</span>
                  <button onClick={() => toggleCategory(c)} className="ml-1.5 text-gray-400 hover:text-red-500 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gig Title */}
      <div className="mb-6">
        <label htmlFor="gigTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Gig Title
        </label>
        <input
          type="text"
          id="gigTitle"
          className="w-full border border-gray-300 dark:border-[#32353c] rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#7295B8] dark:bg-[#0b0e14] dark:text-white transition-colors"
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Enter gig title..."
        />
      </div>

      {/* Tags (Keywords) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tags</label>
          <button
            type="button"
            onClick={() => setShowNewTagInput(!showNewTagInput)}
            className="text-xs text-[#174568] dark:text-blue-400 border border-[#174568] dark:border-blue-400 hover:bg-[#174568] dark:hover:bg-blue-600 hover:text-white rounded-full px-3 py-1 transition-colors"
          >
            + New Tag
          </button>
        </div>

        {/* Inline New Tag Creation */}
        {showNewTagInput && (
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newTagInput}
              onChange={e => setNewTagInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newTagInput.trim()) {
                  createTagMutation.mutate(newTagInput.trim());
                }
              }}
              placeholder="Tag name..."
              className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-[#32353c] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7295B8] dark:bg-[#0b0e14] dark:text-white"
              autoFocus
            />
            <button
              type="button"
              onClick={() => {
                if (newTagInput.trim()) createTagMutation.mutate(newTagInput.trim());
              }}
              disabled={createTagMutation.isPending || !newTagInput.trim()}
              className="px-3 py-1.5 text-sm bg-[#174568] text-white rounded-md hover:bg-[#0f3350] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createTagMutation.isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowNewTagInput(false); setNewTagInput(''); }}
              className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-[#1d2026] text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-[#32353c] transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Existing Tags */}
        {loadingTags ? (
          <p className="text-gray-400 text-sm">Loading tags...</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map(tag => (
              <button
                key={tag._id}
                type="button"
                className={`rounded-full px-4 py-2 text-sm border-[1px] border-[#7295B8] dark:border-[#32353c] transition-colors ${
                  formData.keywords.includes(tag._id)
                    ? 'bg-[#4B718F] dark:bg-blue-600 text-white'
                    : 'text-[#7295B8] dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-[#1d2026]'
                }`}
                onClick={() => toggleTag(tag)}
              >
                {tag.name}
              </button>
            ))}
            {tags.length === 0 && (
              <p className="text-gray-400 text-sm">No tags yet. Create one with "+ New Tag".</p>
            )}
          </div>
        )}

        {/* Selected Tags summary */}
        {formData.keywords.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            {formData.keywords.length} tag{formData.keywords.length > 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {/* Image Upload Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gig Images</label>
        <div className="flex flex-wrap gap-4">
          {formData.images.length < 4 && (
            <div
              className="w-32 h-32 bg-[#D9D9D966] dark:bg-[#0b0e14] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 dark:hover:bg-[#1d2026] border-2 border-dashed border-gray-300 dark:border-[#32353c] transition-colors"
              onClick={() => fileInputRef.current.click()}
            >
              <svg className="w-8 h-8 text-[#174568] dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">Add Image</span>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
                multiple
              />
            </div>
          )}

          {formData.images.map((image, index) => (
            <div key={image.id || index} className="relative w-32 h-32">
              <div className="w-full h-full relative rounded-lg overflow-hidden border border-gray-200 dark:border-[#32353c]">
                {image.loading && (
                   <div className="absolute inset-0 bg-white/60 dark:bg-black/60 backdrop-blur-[1px] z-10 flex flex-col items-center justify-center p-2">
                     <div className="w-6 h-6 border-2 border-[#174568]/30 dark:border-blue-400/30 border-t-[#174568] dark:border-t-blue-400 rounded-full animate-spin mb-1"></div>
                     <span className="text-[10px] font-bold text-[#174568] dark:text-blue-400">UPLOADING</span>
                   </div>
                )}
                <img
                  src={image.preview}
                  alt={`Upload ${index}`}
                  className="w-full h-full object-cover"
                />
              </div>
              {!image.loading && (
                <button
                  type="button"
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-600 transition-colors z-20"
                  onClick={() => removeImage(index)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {index === 0 && (
                <span className="absolute bottom-1 left-1 text-xs bg-black bg-opacity-60 text-white px-1 rounded">Cover</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Proceed Button */}
      <div className="flex justify-end">
        <button
          type="button"
          className={`px-6 py-2 rounded-md transition-colors ${
            isNextEnabled()
              ? 'bg-[#4B718F] dark:bg-blue-600 text-white hover:bg-gray-800 dark:hover:bg-blue-700'
              : 'bg-gray-300 dark:bg-[#1d2026] text-gray-500 dark:text-gray-500 cursor-not-allowed border dark:border-[#32353c]'
          }`}
          onClick={() => setCurrentStep(2)}
          disabled={!isNextEnabled()}
        >
          Proceed
        </button>
      </div>

      {/* Click outside to close dropdowns */}
      {(hellocianDropdownOpen || categoryDropdownOpen) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setHellocianDropdownOpen(false);
            setCategoryDropdownOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default Step1;